import { z } from 'zod'

import type { StatusTone } from '@ems/ui/patterns/status-badge'

import { api } from '#admin/lib/api-client.ts'

/**
 * The program and the marketplace, as the organizer sees them.
 *
 * The real logic in this file is `gridState` and `participation`, and both are here rather than
 * in a component because each answers a question the screen exists to ask and neither is
 * visible in the data.
 *
 * **Draft-then-publish is one word on a row.** A console that rendered slots without saying
 * which half they were in would let an operator build a day's grid and then wonder all
 * afternoon why nobody was requesting meetings.
 *
 * **A speaker's participation has three states, not two.** A null `isEnabled` means "not yet
 * configured", which is *enabled*; rendering it as "off" would show an organizer a marketplace
 * full of refusals that do not exist, and they would spend the week chasing speakers who had
 * never said no.
 */

export const slotSchema = z.object({
  id: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  label: z.string().nullable(),
  isBreak: z.boolean(),
  status: z.enum(['draft', 'published']),
  sortOrder: z.number().int(),
})

export const slotListSchema = z.object({ items: z.array(slotSchema) })

export const meetingSchema = z.object({
  id: z.string(),
  timeSlotId: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
  speakerId: z.string(),
  speakerName: z.string(),
  status: z.enum(['pending', 'accepted', 'rejected', 'canceled', 'no_show']),
  subject: z.string().nullable(),
  roomName: z.string().nullable(),
  tableCode: z.string().nullable(),
  attendees: z.array(
    z.object({ id: z.string().nullable(), name: z.string(), isRequester: z.boolean() }),
  ),
})

export const meetingListSchema = z.object({ items: z.array(meetingSchema) })

export const speakerProfileSchema = z.object({
  speakerId: z.string(),
  name: z.string(),
  headline: z.string().nullable(),
  company: z.string().nullable(),
  /** Null is "not yet configured", which is enabled. Never render it as "off". */
  isEnabled: z.boolean().nullable(),
  onboarded: z.boolean(),
  availableSlotIds: z.array(z.string()),
  /** False: ticketing is on and the speaker holds no speaker pass. Null: none is required. */
  hasPass: z.boolean().nullable(),
})

export const speakerProfileListSchema = z.object({ items: z.array(speakerProfileSchema) })

// ponytail: mirrors `SPEAKER_ROLE` in the API's `rundown/schema.ts`, which admin does not import.
export const SPEAKER_ROLES = ['speaker', 'moderator', 'panellist', 'host'] as const

export const sessionSchema = z.object({
  id: z.string(),
  trackId: z.string().nullable(),
  roomId: z.string().nullable(),
  title: z.string(),
  summary: z.string().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: z.enum(['draft', 'published', 'canceled']),
  requiresRegistration: z.boolean(),
  sortOrder: z.number().int(),
  speakers: z.array(
    z.object({ speakerId: z.string(), name: z.string(), role: z.enum(SPEAKER_ROLES) }),
  ),
})

export const sessionListSchema = z.object({ items: z.array(sessionSchema) })

export const roomSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  capacity: z.number().int().nullable(),
  meetingCapacity: z.number().int(),
  isActive: z.boolean(),
})

export const roomListSchema = z.object({ items: z.array(roomSchema) })

export const trackSchema = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  sortOrder: z.number().int(),
})

export const trackListSchema = z.object({ items: z.array(trackSchema) })

/** A deck or a link on a session: exactly one of `objectKey` and `linkUrl` is set. */
export const materialSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  title: z.string(),
  objectKey: z.string().nullable(),
  linkUrl: z.string().nullable(),
  isPublic: z.boolean(),
  sortOrder: z.number().int(),
})

export const materialListSchema = z.object({ items: z.array(materialSchema) })

/** A `rundown` speaker: someone on the program. Not the CMS's marketing speaker collection. */
export const speakerSchema = z.object({
  id: z.string(),
  personId: z.string().nullable(),
  name: z.string(),
  headline: z.string().nullable(),
  company: z.string().nullable(),
  bio: z.string().nullable(),
  photoKey: z.string().nullable(),
  isPublished: z.boolean(),
  sortOrder: z.number().int(),
})

/**
 * The operator's list row: whether the speaker's person holds a speaker pass. `false` is someone
 * to issue one to; `null` means ticketing is off and no pass is asked for.
 */
export const listedSpeakerSchema = speakerSchema.extend({ hasPass: z.boolean().nullable() })

export const speakerListSchema = z.object({ items: z.array(listedSpeakerSchema) })

export const issuedSpeakerPassSchema = z.object({
  personId: z.string(),
  registrationId: z.string(),
  ticketId: z.string(),
  /** The person already held a pass, and this is it: nothing new was issued. */
  alreadyHeld: z.boolean(),
})

export type Slot = z.infer<typeof slotSchema>
export type Meeting = z.infer<typeof meetingSchema>
export type SpeakerProfile = z.infer<typeof speakerProfileSchema>
export type ProgramSession = z.infer<typeof sessionSchema>
export type Room = z.infer<typeof roomSchema>
export type Track = z.infer<typeof trackSchema>
export type Material = z.infer<typeof materialSchema>
export type Speaker = z.infer<typeof speakerSchema>
export type ListedSpeaker = z.infer<typeof listedSpeakerSchema>
export type SpeakerRole = (typeof SPEAKER_ROLES)[number]

const signalOf = (signal?: AbortSignal) => (signal ? { signal } : {})

/**
 * The **admin** grid, which includes the draft.
 *
 * The public endpoint returns published slots only — correct for a delegate, and an empty
 * screen for the operator who is halfway through building tomorrow.
 */
export function fetchGrid(signal?: AbortSignal) {
  return api.get('/v1/business-matching/admin/grid', {
    schema: slotListSchema,
    ...signalOf(signal),
  })
}

export function fetchMeetings(signal?: AbortSignal) {
  return api.get('/v1/business-matching/admin/meetings', {
    schema: meetingListSchema,
    ...signalOf(signal),
  })
}

export function fetchSpeakerProfiles(signal?: AbortSignal) {
  return api.get('/v1/business-matching/admin/speakers', {
    schema: speakerProfileListSchema,
    ...signalOf(signal),
  })
}

export function fetchSessions(signal?: AbortSignal) {
  return api.get('/v1/rundown/admin/sessions', { schema: sessionListSchema, ...signalOf(signal) })
}

export function fetchRooms(signal?: AbortSignal) {
  return api.get('/v1/room/admin/rooms', { schema: roomListSchema, ...signalOf(signal) })
}

export function fetchTracks(signal?: AbortSignal) {
  return api.get('/v1/rundown/admin/tracks', { schema: trackListSchema, ...signalOf(signal) })
}

/** Every speaker, unpublished included: the session dialog's picker. */
export function fetchSpeakers(signal?: AbortSignal) {
  return api.get('/v1/rundown/admin/speakers', { schema: speakerListSchema, ...signalOf(signal) })
}

const sessionsBase = '/v1/rundown/admin/sessions'

/** The fields the session dialog edits, as the API takes them. Times carry the edition's offset. */
export type SessionBody = {
  title: string
  summary: string | null
  startsAt: string
  endsAt: string
  trackId: string | null
  roomId: string | null
  requiresRegistration: boolean
  sortOrder: number
  status: ProgramSession['status']
}

export function createSession(body: Omit<SessionBody, 'status'>) {
  return api.post(sessionsBase, { body, schema: sessionSchema })
}

export function updateSession(id: string, body: Partial<SessionBody>) {
  return api.patch(`${sessionsBase}/${id}`, { body, schema: sessionSchema })
}

export function deleteSession(id: string) {
  return api.delete(`${sessionsBase}/${id}`, { schema: z.object({ deleted: z.boolean() }) })
}

/**
 * Put a speaker on a session, or change their role: the same idempotent PUT. `sortOrder` is their
 * place in the session's list, sent every time because the PUT overwrites it.
 */
export function setSessionSpeaker(
  sessionId: string,
  body: { speakerId: string; role: SpeakerRole; sortOrder: number },
) {
  return api.put(`${sessionsBase}/${sessionId}/speakers`, {
    body,
    schema: z.object({ attached: z.boolean() }),
  })
}

export function removeSessionSpeaker(sessionId: string, speakerId: string) {
  return api.delete(`${sessionsBase}/${sessionId}/speakers/${speakerId}`, {
    schema: z.object({ removed: z.boolean() }),
  })
}

/** Every material on a session, unshared ones included. */
export function fetchMaterials(sessionId: string, signal?: AbortSignal) {
  return api.get(`${sessionsBase}/${sessionId}/materials`, {
    schema: materialListSchema,
    ...signalOf(signal),
  })
}

// ponytail: links only. A file needs a download the console and the public can open, and nothing
// presigns a `session-material` key yet; add `objectKey` here when something does.
export type MaterialBody = { title: string; linkUrl: string; isPublic: boolean; sortOrder: number }

export function addMaterial(sessionId: string, body: MaterialBody) {
  return api.post(`${sessionsBase}/${sessionId}/materials`, { body, schema: materialSchema })
}

export function deleteMaterial(id: string) {
  return api.delete(`/v1/rundown/admin/materials/${id}`, {
    schema: z.object({ deleted: z.boolean() }),
  })
}

const tracksBase = '/v1/rundown/admin/tracks'

export type TrackBody = { name: string; color: string | null }

export function createTrack(body: TrackBody) {
  return api.post(tracksBase, { body, schema: trackSchema })
}

export function updateTrack(id: string, body: Partial<TrackBody>) {
  return api.patch(`${tracksBase}/${id}`, { body, schema: trackSchema })
}

export function deleteTrack(id: string) {
  return api.delete(`${tracksBase}/${id}`, { schema: z.object({ deleted: z.boolean() }) })
}

const roomsBase = '/v1/room/admin/rooms'

/** The fields the room dialog edits. Location and sort order are left as they are. */
export type RoomBody = Omit<Room, 'id'>

export function createRoom(body: RoomBody) {
  return api.post(roomsBase, { body, schema: roomSchema })
}

export function updateRoom(id: string, body: Partial<RoomBody>) {
  return api.patch(`${roomsBase}/${id}`, { body, schema: roomSchema })
}

export function deleteRoom(id: string) {
  return api.delete(`${roomsBase}/${id}`, { schema: z.object({ deleted: z.boolean() }) })
}

const speakersBase = '/v1/rundown/admin/speakers'

/** The fields the speaker dialog edits, as the API takes them. */
export type SpeakerBody = Omit<Speaker, 'id'>

export function createSpeaker(body: SpeakerBody) {
  return api.post(speakersBase, { body, schema: speakerSchema })
}

export function updateSpeaker(id: string, body: Partial<SpeakerBody>) {
  return api.patch(`${speakersBase}/${id}`, { body, schema: speakerSchema })
}

export function deleteSpeaker(id: string) {
  return api.delete(`${speakersBase}/${id}`, { schema: z.object({ deleted: z.boolean() }) })
}

/**
 * Issue the speaker a free speaker pass (PASS_ACCESS_PLAN.md A4). `email` only for a speaker with
 * no linked person; one already linked is issued on that person. Idempotent.
 */
export function issueSpeakerPass(id: string, body: { email?: string }) {
  return api.post(`${speakersBase}/${id}/pass`, { body, schema: issuedSpeakerPassSchema })
}

export type GridState = 'empty' | 'draft' | 'published' | 'mixed'

/**
 * Which half of draft-then-publish this grid is in.
 *
 * `mixed` is a real and ordinary state, not an error: an operator adding tomorrow's draft to
 * today's live grid has both. Collapsing it to one or the other would either claim the day is
 * bookable when half of it is not, or claim nothing is while delegates are already booking.
 */
export function gridState(slots: readonly Slot[]): GridState {
  if (slots.length === 0) return 'empty'
  const published = slots.filter((s) => s.status === 'published').length
  if (published === 0) return 'draft'
  if (published === slots.length) return 'published'
  return 'mixed'
}

/**
 * What a speaker's participation actually means, in words.
 *
 * A missing speaker pass comes first: with ticketing on, the API refuses every request to that
 * speaker whatever else is true, and the organizer's job is to issue the pass.
 *
 * Then three outcomes, and the order matters: an explicit refusal is reported as a refusal, and
 * everything else is reported by whether the speaker has any slots open — because that is what
 * a delegate will actually run into. A speaker who never answered and opened six slots is
 * bookable, and the label says so without claiming they confirmed anything.
 */
export function participation(profile: SpeakerProfile): { label: string; bookable: boolean } {
  if (profile.hasPass === false) return { label: 'No speaker pass', bookable: false }
  if (profile.isEnabled === false) return { label: 'Opted out', bookable: false }
  if (profile.availableSlotIds.length === 0) {
    return { label: profile.onboarded ? 'No slots open' : 'Not onboarded', bookable: false }
  }
  return {
    label: profile.isEnabled === true ? 'Taking meetings' : 'Open (not yet confirmed)',
    bookable: true,
  }
}

/** Where an accepted meeting is held, or nothing at all while it is still pending. */
export function locationOf(meeting: Meeting): string | null {
  if (meeting.roomName === null) return null
  return meeting.tableCode === null
    ? meeting.roomName
    : `${meeting.roomName} · ${meeting.tableCode}`
}

/**
 * A session's publication state, as a tone.
 *
 * `draft` is neutral rather than a warning: an unpublished session is the ordinary state of an
 * agenda being built, not something anybody needs to act on today.
 */
const SESSION_TONE: Record<ProgramSession['status'], StatusTone> = {
  published: 'success',
  draft: 'neutral',
  // A canceled session stays on the agenda so somebody can see it was canceled rather than
  // wonder where it went.
  canceled: 'danger',
}

export function sessionTone(status: ProgramSession['status']): StatusTone {
  return SESSION_TONE[status]
}

/**
 * A meeting's state, as a tone.
 *
 * `pending` is the only one that is anybody's move, and it is the desk's — so it is `warning`
 * rather than `pending`, which would read as "waiting on the delegate" when the delegate has
 * already asked and is waiting on us.
 *
 * `no_show` is `danger`: somebody blocked a slot and a room and did not arrive, which is the one
 * outcome on this list that costs the organizer something.
 */
const MEETING_TONE: Record<Meeting['status'], StatusTone> = {
  accepted: 'success',
  pending: 'warning',
  rejected: 'neutral',
  canceled: 'neutral',
  no_show: 'danger',
}

export function meetingTone(status: Meeting['status']): StatusTone {
  return MEETING_TONE[status]
}
